import { useCallback, useEffect, useRef, useState } from "react";

const POLL_INTERVAL = 5000;
const MAX_POLL_ATTEMPTS = 60;

/**
 * Single shared source for document analysis in onboarding.
 * - Fetches once per tenant/version and caches the result.
 * - Back/forward navigation reuses the cache (no refetch).
 * - Call refresh() after upload/delete/clave to invalidate and repoll.
 */
export default function useDocumentAnalysis(tenantId, checkFn, enabled = true) {
  const [analysis, setAnalysis] = useState({});
  const [status, setStatus] = useState("idle");
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(0);
  const timeoutRef = useRef(null);
  const lastTenantRef = useRef(null);
  const checkFnRef = useRef(checkFn);
  const cacheRef = useRef({ tenantId: null, version: -1, status: "idle" });

  checkFnRef.current = checkFn;

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const refresh = useCallback(() => {
    clearTimer();
    setAnalysis({});
    setStatus("idle");
    setLoading(false);
    setVersion((currentVersion) => currentVersion + 1);
  }, [clearTimer]);

  useEffect(() => {
    const isNewTenant = lastTenantRef.current !== tenantId;

    if (isNewTenant) {
      lastTenantRef.current = tenantId;
      cacheRef.current = { tenantId, version: -1, status: "idle" };
      setAnalysis({});
      setStatus("idle");
      setLoading(false);
    }

    if (!enabled || !tenantId || typeof checkFnRef.current !== "function") {
      clearTimer();
      setLoading(false);
      return;
    }

    const cached = cacheRef.current;
    if (
      cached.tenantId === tenantId &&
      cached.version === version &&
      (cached.status === "done" || cached.status === "failed")
    ) {
      return;
    }

    let cancelled = false;
    let pollAttempts = 0;

    setLoading(true);
    setStatus("processing");

    const finishWithFailure = () => {
      cacheRef.current = { tenantId, version, status: "failed" };
      setStatus("failed");
      setLoading(false);
      clearTimer();
    };

    const pollOnce = async () => {
      pollAttempts += 1;

      try {
        const res = await checkFnRef.current(tenantId);
        if (cancelled) return;

        const apiStatus = res?.success_res?.status;
        const documentAnalysis = res?.success_res?.document_analysis;

        if (apiStatus === "done") {
          setAnalysis(documentAnalysis ?? {});
          setStatus("done");
          setLoading(false);
          cacheRef.current = { tenantId, version, status: "done" };
          clearTimer();
          return;
        }

        if (apiStatus === "failed") {
          finishWithFailure();
          return;
        }

        // Support the legacy response shape where analysis was returned
        // without an explicit status.
        if (
          !apiStatus &&
          documentAnalysis &&
          Object.keys(documentAnalysis).length > 0
        ) {
          setAnalysis(documentAnalysis);
          setStatus("done");
          setLoading(false);
          cacheRef.current = { tenantId, version, status: "done" };
          clearTimer();
          return;
        }

        // A null or unknown response is an error, not an in-progress job.
        if (apiStatus !== "processing" || pollAttempts >= MAX_POLL_ATTEMPTS) {
          finishWithFailure();
          return;
        }

        timeoutRef.current = setTimeout(pollOnce, POLL_INTERVAL);
      } catch {
        if (!cancelled) finishWithFailure();
      }
    };

    // After an upload/delete refresh, give the backend time to replace the
    // previous job status. Polling immediately can read and cache a stale
    // terminal status from the job that was just superseded.
    if (version > 0) {
      timeoutRef.current = setTimeout(pollOnce, POLL_INTERVAL);
    } else {
      pollOnce();
    }

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [tenantId, version, enabled, clearTimer]);

  return { analysis, status, loading, refresh };
}
